import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import Layout from "../../../components/Layout/Layout";
import cdpService from "../../../apiService/cdp.service";
import CustomerPersonalInfo from "../../../components/CDP/Customers/Profile/CustomerPersonalInfo";
import CustomerActivePlan from "../../../components/CDP/Customers/Profile/CustomerActivePlan";
import CustomerSubscriptionHistory from "../../../components/CDP/Customers/Profile/CustomerSubscriptionHistory";

export default function CustomerProfile() {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState(10);

  useEffect(() => {
    async function fetchCustomer() {
      if (details) {
        setHistoryLoading(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await cdpService.getCustomerReportDetail(id, {
          page: historyPage,
          length: historyPageSize,
        });
        setDetails(response.data?.data?.reportData || null);
      } catch (error) {
        if (!details) setDetails(null);
      } finally {
        setLoading(false);
        setHistoryLoading(false);
      }
    }

    fetchCustomer();
    // `details` is intentionally excluded to avoid refetching after each response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, historyPage, historyPageSize]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen p-6">
          <p className="text-sm text-gray-500">Loading customer...</p>
        </div>
      </Layout>
    );
  }

  if (!details?.basicDetails) {
    return (
      <Layout>
        <div className="min-h-screen p-6">
          <p className="text-sm text-gray-500">Customer not found.</p>
          <Link to="/cdp/customers" className="text-blue-600 text-sm">Back to Customers</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen p-6">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl bg-white px-6 py-5 shadow-sm ring-1 ring-gray-100">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Details</h1>
              <p className="mt-1 text-sm text-gray-500">
                Complete profile and subscription information for {details.basicDetails.name || details.basicDetails.sso_code}
              </p>
            </div>
            <Link
              to="/cdp/customers"
              className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Back to Customers
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
            <div className="lg:col-span-1">
              <CustomerPersonalInfo customer={details.basicDetails} />
            </div>
            <div className="lg:col-span-2">
              <CustomerActivePlan activePlan={details.activeSubscription} />
            </div>
          </div>

          <CustomerSubscriptionHistory
            history={details.purchaseHistory}
            loading={historyLoading}
            pageSize={historyPageSize}
            onPageChange={setHistoryPage}
            onPageSizeChange={(size) => {
              setHistoryPageSize(size);
              setHistoryPage(1);
            }}
          />
        </div>
      </div>
    </Layout>
  );
}
